import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { UserRole } from '@/types/user-roles';
import { Info, Building2, Search } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface UserRoleSelectionProps {
  role: UserRole;
  onRoleChange: (value: UserRole) => void;
  selectedProviderId?: string;
  onProviderChange?: (providerId: string) => void;
}

interface PEOProvider {
  id: string;
  name: string;
  referral_code?: string;
}

export const UserRoleSelection = ({ 
  role, 
  onRoleChange, 
  selectedProviderId, 
  onProviderChange 
}: UserRoleSelectionProps) => {
  const [providers, setProviders] = useState<PEOProvider[]>([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (role === 'peo') {
      fetchPEOProviders();
    }
  }, [role]);

  const fetchPEOProviders = async () => {
    setIsLoadingProviders(true);
    try {
      const { data, error } = await supabase
        .from('peo_providers')
        .select('id, name, referral_code')
        .order('name');

      if (error) {
        console.error('Error fetching PEO providers:', error);
      } else {
        setProviders(data || []);
      }
    } catch (error) {
      console.error('Error fetching PEO providers:', error);
    } finally {
      setIsLoadingProviders(false);
    }
  };

  const filteredProviders = useMemo(() => {
    if (!searchTerm) return providers;
    const search = searchTerm.toLowerCase();
    return providers.filter(provider => 
      provider.name.toLowerCase().includes(search) ||
      provider.referral_code?.toLowerCase().includes(search)
    );
  }, [providers, searchTerm]);

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-gray-700">What user type best describes you?</p>
      <RadioGroup
        value={role}
        onValueChange={(value) => onRoleChange(value as UserRole)}
        className="space-y-2"
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="regular" id="regular" />
          <Label htmlFor="regular">PEO Shopper</Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="broker" id="broker" />
          <Label htmlFor="broker">Broker or Referral Partner</Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="peo" id="peo" />
          <Label htmlFor="peo">PEO Provider</Label>
        </div>
      </RadioGroup>
      
      {role === 'peo' && (
        <div className="space-y-4">
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md flex items-start space-x-2 transition-all duration-200 ease-in-out">
            <Info className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-green-800">
              <strong>Email Verification Required:</strong> PEO Providers must use their company email address (@yourcompany.com) for verification.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Select Your PEO Provider
            </Label>
            
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search PEO providers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Select 
              value={selectedProviderId} 
              onValueChange={onProviderChange}
              disabled={isLoadingProviders}
            >
              <SelectTrigger>
                <SelectValue placeholder={isLoadingProviders ? "Loading providers..." : "Choose your PEO provider"} />
              </SelectTrigger>
              <SelectContent className="max-h-[200px]">
                {filteredProviders.length === 0 ? (
                  <div className="p-2 text-sm text-muted-foreground text-center">
                    {searchTerm ? 'No providers found matching your search' : 'No providers available'}
                  </div>
                ) : (
                  filteredProviders.map((provider) => (
                    <SelectItem key={provider.id} value={provider.id}>
                      <div className="flex items-center justify-between w-full">
                        <span>{provider.name}</span>
                        {provider.referral_code && (
                          <span className="text-xs text-muted-foreground ml-2 font-mono">
                            {provider.referral_code}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Select the PEO provider you want to join. Your request will be reviewed by an administrator.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};